import { Component, input, output, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ModalComponent } from '../../../shared/modal/modal.component';
import { AuthApiService } from '../../../core/services/auth-api.service';
import { AuthService } from '../../../core/services/auth.service';
import {
  FirebaseSsoService,
  firebaseAuthErrorMessage,
} from '../../../core/services/firebase-sso.service';
import type { AuthResponse } from '../../../core/types/auth.types';
import { FORM_FIELD_LIMITS } from '../../../core/constants/form-field-limits';
import { exceedsMaxLength, isValidEmailFormat } from '../../../core/utils/form-field-validation';

@Component({
  selector: 'app-sign-up-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  templateUrl: './sign-up-modal.component.html',
  styleUrl: './sign-up-modal.component.scss',
})
export class SignUpModalComponent {
  readonly limits = FORM_FIELD_LIMITS;

  isOpen = input.required<boolean>();

  closed = output<void>();
  loginClick = output<void>();
  registerSuccess = output<void>();
  registerError = output<string>();

  private authApi = inject(AuthApiService);
  private authService = inject(AuthService);
  private firebaseSso = inject(FirebaseSsoService);
  private router = inject(Router);

  fullName = signal('');
  email = signal('');
  password = signal('');
  showPassword = signal(false);
  termsAccepted = signal(true);
  loading = signal(false);
  errorMessage = signal('');

  onClose(): void {
    this.clearError();
    this.closed.emit();
  }

  clearError(): void {
    this.errorMessage.set('');
  }

  togglePassword(): void {
    this.showPassword.update((v) => !v);
  }

  onSubmit(): void {
    this.errorMessage.set('');
    const name = this.fullName().trim();
    const e = this.email().trim();
    const p = this.password();
    if (!e || !p) {
      this.errorMessage.set('Preencha e-mail e senha.');
      return;
    }
    if (exceedsMaxLength(name, this.limits.fullName)) {
      this.errorMessage.set(`O nome pode ter no máximo ${this.limits.fullName} caracteres.`);
      return;
    }
    if (!isValidEmailFormat(e)) {
      this.errorMessage.set('Informe um e-mail válido.');
      return;
    }
    if (p.length > this.limits.passwordMax) {
      this.errorMessage.set(`A senha deve ter no máximo ${this.limits.passwordMax} caracteres.`);
      return;
    }
    if (!this.isPasswordStrong(p)) {
      const missing = this.getMissingPasswordRequirements(p);
      this.errorMessage.set(`A senha ainda precisa de: ${missing.join(', ')}.`);
      return;
    }
    if (!this.termsAccepted()) {
      this.errorMessage.set('Você precisa concordar com os Termos de Serviço e a Política de Privacidade.');
      return;
    }
    this.loading.set(true);
    const generatedUsername = this.generateUsernameFromEmail(e);
    this.authApi
      .register({ email: e, password: p, fullName: name || undefined, username: generatedUsername })
      .pipe(switchMap(() => this.authApi.login({ email: e, password: p })))
      .subscribe({
        next: (res) => {
          this.loading.set(false);
          if (res?.data) {
            this.authService.setSession(res as AuthResponse);
          }
          this.registerSuccess.emit();
          this.onClose();
        },
        error: (err) => {
          this.loading.set(false);
          const message =
            err?.error?.message ??
            err?.error?.error ??
            (typeof err?.error === 'string' ? err.error : null) ??
            'Falha no cadastro ou ao entrar. Tente novamente.';
          this.errorMessage.set(message);
          this.registerError.emit(message);
        },
      });
  }

  private generateUsernameFromEmail(email: string): string {
    const localPart = email.split('@')[0]?.trim().toLowerCase() ?? '';
    const normalized = localPart
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9._-]/g, '')
      .replace(/[._-]{2,}/g, '_')
      .replace(/^[._-]+|[._-]+$/g, '');
    if (normalized.length >= 3) {
      return normalized.slice(0, 30);
    }
    return `user_${Date.now().toString(36)}`;
  }

  private isPasswordStrong(password: string): boolean {
    return this.getMissingPasswordRequirements(password).length === 0;
  }

  private getMissingPasswordRequirements(password: string): string[] {
    const missing: string[] = [];
    const hasMinLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[^A-Za-z0-9\s]/.test(password);
    const hasWhitespace = /\s/.test(password);

    if (!hasMinLength) missing.push('pelo menos 8 caracteres');
    if (!hasUppercase) missing.push('1 letra maiúscula');
    if (!hasLowercase) missing.push('1 letra minúscula');
    if (!hasNumber) missing.push('1 número');
    if (!hasSpecial) missing.push('1 caractere especial');
    if (hasWhitespace) missing.push('remover espaços em branco');

    return missing;
  }

  onLoginClick(): void {
    this.onClose();
    this.loginClick.emit();
  }

  onTermsClick(): void {
    this.onClose();
    void this.router.navigateByUrl('/terms');
  }

  onPrivacyClick(): void {
    this.onClose();
    void this.router.navigateByUrl('/privacy');
  }

  onGoogleSignUp(): void {
    this.startFirebaseSso(() => this.firebaseSso.signInWithGoogle());
  }

  onAppleSignUp(): void {
    this.startFirebaseSso(() => this.firebaseSso.signInWithApple());
  }

  private startFirebaseSso(getToken: () => Observable<string>): void {
    if (!this.firebaseSso.isConfigured()) {
      this.errorMessage.set(
        'Cadastro social não está configurado. Defina `firebase` em `environment` e o endpoint da API (ver docs/firebase-sso-setup.md).',
      );
      return;
    }
    this.errorMessage.set('');
    this.loading.set(true);
    getToken()
      .pipe(switchMap((idToken) => this.authApi.loginWithFirebase({ idToken })))
      .subscribe({
        next: (res) => {
          this.loading.set(false);
          if (res?.data) {
            this.authService.setSession(res as AuthResponse);
          }
          this.registerSuccess.emit();
          this.onClose();
        },
        error: (err) => {
          this.loading.set(false);
          const apiMsg =
            err?.error?.message ??
            err?.error?.error ??
            (typeof err?.error === 'string' ? err.error : null);
          const message =
            typeof apiMsg === 'string' && apiMsg.length > 0
              ? apiMsg
              : firebaseAuthErrorMessage(err);
          this.errorMessage.set(message);
          this.registerError.emit(message);
        },
      });
  }
}
